import React, { useState, useEffect } from 'react';
import { isStaff } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { acceptInvite } from '../../services/authService';
import toast from 'react-hot-toast';

/* Регистрация по ссылке-приглашению. Оформление и шаги — те же, что во вкладке
   «Регистрация» на лендинге: сначала имя и фамилия, потом почта и пароль.
   Если админ уже завёл ученика, имя подставляется из приглашения (но его
   можно поправить), а почта закреплена — приглашение выписано именно на неё. */

const ASSET = (f) => `${process.env.PUBLIC_URL || ''}/landing/${f}`;

const IcArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const IcBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const IcEyeOn = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const IcEyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [agreePolicy, setAgreePolicy]   = useState(false);
  const [agreeConsent, setAgreeConsent] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [checking, setChecking]   = useState(false);
  const [step, setStep]           = useState(0);   // 0 — имя/фамилия, 1 — почта/пароль
  const [dir, setDir]             = useState('fwd');
  const { register, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && user) navigate(isStaff(user.role) ? '/admin' : '/');
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) return;
    setChecking(true);
    acceptInvite(token)
      .then(res => {
        setInviteInfo({ ...res, token });
        setEmail(res.email || '');
        // Имя, которое админ ввёл при добавлении — подставляем, но не запрещаем менять
        const parts = (res.name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length) { setFirstName(parts[0]); setLastName(parts.slice(1).join(' ')); }
      })
      .catch(() => toast.error('Приглашение недействительно или истекло'))
      .finally(() => setChecking(false));
  }, [location.search]);

  const nameValid = firstName.trim().length >= 1 && lastName.trim().length >= 1;
  const goNext = () => { if (nameValid) { setDir('fwd'); setStep(1); } };
  const goBack = () => { setDir('back'); setStep(0); };
  const onNameKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 0) return goNext();
    if (password.length < 6) return toast.error('Пароль должен быть не менее 6 символов');
    if (!agreePolicy || !agreeConsent) return toast.error('Подтвердите согласие с документами');
    setLoading(true);
    try {
      await register({
        name: `${firstName.trim()} ${lastName.trim()}`,
        email, password, token: inviteInfo?.token,
      });
      window.location.href = '/';
    } catch {
      // сообщение показывает register()
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rg">
      <style>{`
        .rg { position: relative; min-height: 100vh; overflow: hidden; background: #0a0612;
              font-family: 'Montserrat', -apple-system, sans-serif; color: #fff;
              display: flex; align-items: center; justify-content: center; padding: 28px 18px; box-sizing: border-box; }
        .rg-pattern, .rg-glow, .rg-vign { position: absolute; inset: 0; pointer-events: none; }
        .rg-pattern { background: url(${ASSET('pattern-brand.png')}) 0 -10px / 300px auto repeat; opacity: .5; }
        .rg-glow { background:
          radial-gradient(56% 46% at 22% 18%, rgba(124,77,255,0.42) 0%, rgba(124,77,255,0) 62%),
          radial-gradient(50% 44% at 84% 78%, rgba(168,85,247,0.30) 0%, rgba(168,85,247,0) 66%); }
        .rg-vign { box-shadow: inset 0 0 220px 60px rgba(0,0,0,0.7); }

        .rg-inner { position: relative; z-index: 1; width: 100%; max-width: 440px; }
        /* Логотип белый на прозрачном — на светлой плитке он пропадал,
           поэтому берём тот же приём, что на лендинге: инверсия в белый. */
        .rg-brand { display: flex; align-items: center; justify-content: center; margin-bottom: 22px; }
        .rg-brand img { height: 32px; width: auto; display: block; filter: brightness(0) invert(1); }

        .rg-title { font-family: 'Saar SP Demo', sans-serif; font-size: 34px; line-height: 1.06; font-weight: 400;
          letter-spacing: 0.6px; text-transform: uppercase; text-align: center; margin-bottom: 8px; }
        .rg-lead { font-size: 13.5px; color: #a99fc7; text-align: center; line-height: 1.5; margin-bottom: 22px; }
        .rg-lead b { color: #c9bff0; font-weight: 700; }

        .rg-card { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 18px; padding: 22px; backdrop-filter: blur(12px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.4); box-sizing: border-box; }

        .rg-steps { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 18px; }
        .rg-dot { width: 9px; height: 9px; border-radius: 50%; background: rgba(255,255,255,0.18); transition: .3s; }
        .rg-dot.on { background: #8b5cf6; box-shadow: 0 0 10px rgba(139,92,246,0.85); }
        .rg-bar { width: 34px; height: 2px; border-radius: 2px; background: rgba(255,255,255,0.14); transition: .3s; }
        .rg-bar.on { background: #8b5cf6; }

        .rg-stepwrap { animation: rgIn .34s cubic-bezier(.22,.61,.36,1); }
        .rg-stepwrap[data-dir="back"] { animation: rgInBack .34s cubic-bezier(.22,.61,.36,1); }
        @keyframes rgIn { from { opacity: 0; transform: translateX(18px); } to { opacity: 1; transform: none; } }
        @keyframes rgInBack { from { opacity: 0; transform: translateX(-18px); } to { opacity: 1; transform: none; } }

        .rg-field { margin-bottom: 13px; }
        .rg-field label { display: block; font-size: 11px; font-weight: 700; letter-spacing: .5px;
          text-transform: uppercase; color: #a99fc7; margin-bottom: 6px; }
        .rg-input { width: 100%; padding: 12px 14px; box-sizing: border-box; background: rgba(0,0,0,0.30);
          border: 1px solid rgba(255,255,255,0.14); border-radius: 12px; color: #fff;
          font-family: inherit; font-size: 14.5px; outline: none; transition: border-color .15s; }
        .rg-input::placeholder { color: #7c7396; }
        .rg-input:focus { border-color: #8b5cf6; }
        .rg-input:disabled { opacity: .75; cursor: not-allowed; }
        .rg-hint { font-size: 11.5px; color: #8a82a6; margin-top: 6px; }

        .rg-eye { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); width: 26px; height: 26px;
          border: none; background: none; color: #a99fc7; cursor: pointer; display: inline-flex;
          align-items: center; justify-content: center; padding: 0; }
        .rg-eye svg { width: 17px; height: 17px; }

        .rg-next, .rg-btn { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%;
          padding: 14px; border: none; border-radius: 30px; cursor: pointer; font-family: inherit;
          font-size: 15px; font-weight: 700; color: #fff;
          background: linear-gradient(135deg, #7c4dff, #a855f7);
          box-shadow: 0 0 28px rgba(124,77,255,0.7); transition: .18s; }
        .rg-btn { margin-top: 6px; }
        .rg-next:disabled, .rg-btn:disabled { background: rgba(255,255,255,0.07); color: #8a82a6; box-shadow: none; cursor: not-allowed; }
        .rg-next:not(:disabled):hover, .rg-btn:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 0 38px rgba(124,77,255,0.95); }
        .rg-next svg { width: 18px; height: 18px; transition: transform .2s; }
        .rg-next:not(:disabled):hover svg { transform: translateX(4px); }

        .rg-back { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; padding: 0;
          color: #a99fc7; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; margin-bottom: 14px; }
        .rg-back:hover { color: #fff; }
        .rg-back svg { width: 15px; height: 15px; }
        .rg-greet { font-size: 14px; color: #c9bff0; margin-bottom: 16px; line-height: 1.5; }
        .rg-greet b { color: #fff; }

        .rg-chk { display: flex; flex-direction: column; gap: 9px; margin: 4px 0 16px; }
        .rg-chk label { display: flex; gap: 9px; align-items: flex-start; font-size: 12.5px; color: #c9bff0; line-height: 1.45; cursor: pointer; }
        .rg-chk input { margin-top: 2px; accent-color: #7c4dff; flex-shrink: 0; width: 16px; height: 16px; }
        .rg-chk a { color: #a78bfa; font-weight: 600; }

        .rg-foot { margin-top: 16px; font-size: 13px; color: #a99fc7; text-align: center; }
        .rg-foot button { background: none; border: none; color: #b794f6; font-weight: 700; cursor: pointer;
          font-family: inherit; font-size: 13px; padding: 0; }
        .rg-foot button:hover { text-decoration: underline; }

        @media (max-width: 480px) { .rg-title { font-size: 27px; } .rg-card { padding: 18px; } }
      `}</style>

      <div className="rg-pattern" />
      <div className="rg-glow" />
      <div className="rg-vign" />

      <div className="rg-inner">
        <div className="rg-brand">
          <img src={ASSET('logo-hq.png')} alt="ИНФА СОТКА" />
        </div>

        <h1 className="rg-title">Регистрация</h1>
        <p className="rg-lead">
          {checking
            ? 'Проверяем приглашение…'
            : inviteInfo?.course
              ? <>Вас записали на курс <b>«{inviteInfo.course.name}»</b>. Задайте пароль — и можно начинать.</>
              : 'Создайте аккаунт, чтобы начать подготовку к ОГЭ / ЕГЭ по информатике.'}
        </p>

        <div className="rg-card">
          <form onSubmit={handleSubmit} noValidate>
            <div className="rg-steps" aria-hidden="true">
              <span className="rg-dot on" />
              <span className={`rg-bar ${step >= 1 ? 'on' : ''}`} />
              <span className={`rg-dot ${step >= 1 ? 'on' : ''}`} />
            </div>

            <div className="rg-stepwrap" key={step} data-dir={dir}>
              {step === 0 ? (
                <>
                  <div className="rg-field">
                    <label>Имя</label>
                    <input className="rg-input" type="text" value={firstName} placeholder="Иван"
                      onChange={e => setFirstName(e.target.value)} onKeyDown={onNameKey} />
                  </div>
                  <div className="rg-field">
                    <label>Фамилия</label>
                    <input className="rg-input" type="text" value={lastName} placeholder="Иванов"
                      onChange={e => setLastName(e.target.value)} onKeyDown={onNameKey} />
                  </div>
                  <button type="button" className="rg-next" disabled={!nameValid} onClick={goNext}>
                    Далее <IcArrow />
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="rg-back" onClick={goBack}><IcBack />Назад</button>
                  <div className="rg-greet">Приятно познакомиться, <b>{firstName.trim()}</b>! Остался один шаг.</div>

                  <div className="rg-field">
                    <label>Email</label>
                    <input className="rg-input" type="email" value={email} placeholder="student@example.com"
                      onChange={e => setEmail(e.target.value)} required disabled={!!inviteInfo?.email} />
                    {inviteInfo?.email && <div className="rg-hint">Приглашение выписано на эту почту</div>}
                  </div>

                  <div className="rg-field">
                    <label>Пароль</label>
                    <div style={{ position: 'relative' }}>
                      <input className="rg-input" type={showPass ? 'text' : 'password'} value={password}
                        onChange={e => setPassword(e.target.value)} placeholder="••••••••" required
                        minLength={6} style={{ paddingRight: 40 }} />
                      <button type="button" className="rg-eye" onClick={() => setShowPass(v => !v)} aria-label="Показать пароль">
                        {showPass ? <IcEyeOff /> : <IcEyeOn />}
                      </button>
                    </div>
                    <div className="rg-hint">Минимум 6 символов</div>
                  </div>

                  <div className="rg-chk">
                    <label>
                      <input type="checkbox" checked={agreePolicy} onChange={e => setAgreePolicy(e.target.checked)} />
                      <span>Ознакомлен(а) с <a href="/privacy" target="_blank" rel="noopener noreferrer">Политикой обработки персональных данных</a></span>
                    </label>
                    <label>
                      <input type="checkbox" checked={agreeConsent} onChange={e => setAgreeConsent(e.target.checked)} />
                      <span>Даю <a href="/consent" target="_blank" rel="noopener noreferrer">Согласие на обработку персональных данных</a></span>
                    </label>
                  </div>

                  <button type="submit" className="rg-btn" disabled={loading || !agreePolicy || !agreeConsent}>
                    {loading ? 'Подождите...' : 'Зарегистрироваться'}
                  </button>
                </>
              )}
            </div>
          </form>

          <p className="rg-foot">
            Уже есть аккаунт? <button type="button" onClick={() => navigate('/')}>Войти</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
